import React, { useState } from 'react';
import toast, {Toaster} from 'react-hot-toast'

import {
    Button,
    Checkbox,
    Form,
    Alert,
    FormAlert
} from '@patternfly/react-core';
import { BoltIcon, EnvelopeIcon, LockIcon, EyeIcon, EyeSlashIcon } from '@patternfly/react-icons';
import {loginUser, resendConfirmationEmail} from "@app/utils/utils";
import {CreateAccountComponent} from "@app/Login/CreateAccountComponent";
import {BorderedInput} from "@app/Login/BorderedInput";
import {useWindowSize, isMobile, isTablet} from "@app/utils/useWindowSize";

export interface ILoginComponentProps {
    triggerRefresh: Function;
}

const LoginComponent: React.FunctionComponent<ILoginComponentProps> = (props) => {

    const [userNameValue, setUserNameValue] = useState('');
    const [passwordValue, setPasswordValue] = useState('');
    const [isRememberMeChecked, setIsRememberMeChecked] = useState(false);
    const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [emailNotVerified, setEmailNotVerified] = useState(false);
    const [resendSent, setResendSent] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const { width } = useWindowSize();
    const mobile = isMobile(width);
    const tablet = isTablet(width);

    const handleCreateAccountModalToggle = () => {
        setIsCreateAccountModalOpen(!isCreateAccountModalOpen);
    }

    function handleUsernameChange(value: string) {
        setUserNameValue(value);
        setErrorMessage('');
        setEmailNotVerified(false);
        setResendSent(false);
    }

    function handlePasswordChange(value: string) {
        setPasswordValue(value);
        setErrorMessage('');
        setEmailNotVerified(false);
        setResendSent(false);
    }

    function onRememberMeClick() {
        setIsRememberMeChecked(!isRememberMeChecked);
    }

    const onLoginButtonClick = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!userNameValue || !passwordValue) {
            setErrorMessage('Please enter your email and password');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');

        try {
            const data = await loginUser({
                'email': userNameValue,
                'password': passwordValue
            });
            if (data != undefined && data != null) {
                props.triggerRefresh(data);
            }
        } catch (error: any) {
            if (error?.status === 403) {
                setEmailNotVerified(true);
                setErrorMessage('Please verify your email address before logging in.');
            } else {
                setErrorMessage('Invalid email or password');
                toast.error("Invalid username or password");
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendFromLogin = async () => {
        if (!userNameValue) return;
        setIsResending(true);
        try {
            await resendConfirmationEmail(userNameValue);
            setResendSent(true);
        } catch (error) {
            // silently fail
        } finally {
            setIsResending(false);
        }
    };

    const PURPLE_GRADIENT = 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)';

    const styles = {
        pageContainer: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: (mobile ? 'column' : 'row') as any,
            background: '#ffffff'
        },
        leftPanel: {
            flex: mobile ? 'none' : 1,
            display: mobile ? 'none' : 'flex',
            flexDirection: 'column' as const,
            justifyContent: 'center',
            alignItems: 'center',
            padding: tablet ? '24px' : '40px',
            background: PURPLE_GRADIENT,
            position: 'relative' as const,
            overflow: 'hidden'
        },
        leftContent: {
            position: 'relative' as const,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            maxWidth: '420px'
        },
        logoIcon: {
            width: tablet ? '76px' : '88px',
            height: tablet ? '76px' : '88px',
            borderRadius: '22px',
            background: 'rgba(255, 255, 255, 0.18)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)'
        },
        logoIconInner: {
            fontSize: tablet ? '36px' : '44px',
            color: '#fbbf24'
        },
        logoText: {
            fontSize: tablet ? '36px' : '44px',
            fontWeight: 800,
            color: '#ffffff',
            letterSpacing: '-1px',
            margin: 0,
            textAlign: 'center' as const
        },
        logoTagline: {
            fontSize: '13px',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.85)',
            letterSpacing: '4px',
            textTransform: 'uppercase' as const,
            marginTop: '6px',
            marginBottom: '20px'
        },
        taglineDivider: {
            width: '40px',
            height: '2px',
            background: 'rgba(255, 255, 255, 0.5)',
            marginBottom: '20px'
        },
        brandText: {
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: tablet ? '15px' : '16px',
            textAlign: 'center' as const,
            maxWidth: '320px',
            lineHeight: 1.7,
            margin: 0
        },
        rightPanel: {
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: mobile ? '24px' : (tablet ? '32px 32px' : '48px 48px'),
            background: '#ffffff',
            overflowY: 'auto' as const
        },
        formContainer: {
            width: '100%',
            maxWidth: '520px'
        },
        formReset: {
            border: 'none',
            padding: 0,
            background: 'transparent'
        } as React.CSSProperties,
        title: {
            color: '#0f172a',
            fontSize: '34px',
            fontWeight: 700,
            margin: 0,
            marginBottom: '8px',
            textAlign: 'center' as const,
            letterSpacing: '-0.5px'
        },
        subtitle: {
            color: '#6b7280',
            fontSize: '15px',
            margin: 0,
            marginBottom: '24px',
            textAlign: 'center' as const
        },
        fieldLabel: {
            display: 'block',
            fontSize: '14px',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '8px'
        },
        fieldGroup: {
            marginBottom: '16px'
        },
        checkboxContainer: {
            marginTop: '4px',
            marginBottom: '16px'
        },
        submitButton: {
            background: PURPLE_GRADIENT,
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
            width: '100%'
        },
        submitButtonDisabled: {
            background: 'linear-gradient(135deg, #c4b5fd 0%, #a78bfa 100%)',
            border: 'none',
            padding: '13px 24px',
            fontSize: '15px',
            fontWeight: 600,
            borderRadius: '10px',
            boxShadow: 'none',
            cursor: 'not-allowed',
            width: '100%'
        },
        forgotLinkRow: {
            textAlign: 'center' as const,
            marginTop: '14px'
        },
        link: {
            color: '#6366f1',
            textDecoration: 'none',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '14px'
        },
        dividerRow: {
            display: 'flex',
            alignItems: 'center',
            margin: '18px 0',
            color: '#9ca3af',
            fontSize: '13px'
        },
        dividerLine: {
            flex: 1,
            height: '1px',
            background: '#e5e7eb'
        },
        dividerLabel: {
            padding: '0 12px'
        },
        signupRow: {
            textAlign: 'center' as const,
            color: '#4b5563',
            fontSize: '14px'
        },
        footerContainer: {
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'center',
            gap: '32px',
            fontSize: '13px'
        },
        footerLink: {
            color: '#6b7280',
            textDecoration: 'none'
        }
    };

    const isFormValid = userNameValue && passwordValue;

    return (
        <div style={styles.pageContainer}>
            <Toaster />

            {/* Left Panel - Branding with decorations */}
            <div style={styles.leftPanel}>
                {/* Decorative circle arcs - top left */}
                <svg
                    style={{ position: 'absolute', top: '-80px', left: '-80px', opacity: 0.25, zIndex: 1 }}
                    width="320" height="320" viewBox="0 0 320 320" fill="none"
                >
                    <circle cx="160" cy="160" r="80" stroke="white" strokeWidth="1.5" />
                    <circle cx="160" cy="160" r="120" stroke="white" strokeWidth="1.5" />
                    <circle cx="160" cy="160" r="160" stroke="white" strokeWidth="1.5" />
                </svg>

                {/* Dot grid - top right */}
                <svg
                    style={{ position: 'absolute', top: '60px', right: '60px', opacity: 0.35, zIndex: 1 }}
                    width="120" height="80" viewBox="0 0 120 80" fill="none"
                >
                    {Array.from({ length: 5 }).map((_, row) =>
                        Array.from({ length: 8 }).map((_, col) => (
                            <circle
                                key={`tr-${row}-${col}`}
                                cx={col * 15 + 4}
                                cy={row * 15 + 4}
                                r="2"
                                fill="white"
                            />
                        ))
                    )}
                </svg>

                {/* Dot grid - bottom left */}
                <svg
                    style={{ position: 'absolute', bottom: '120px', left: '40px', opacity: 0.3, zIndex: 1 }}
                    width="90" height="60" viewBox="0 0 90 60" fill="none"
                >
                    {Array.from({ length: 4 }).map((_, row) =>
                        Array.from({ length: 6 }).map((_, col) => (
                            <circle
                                key={`bl-${row}-${col}`}
                                cx={col * 14 + 4}
                                cy={row * 14 + 4}
                                r="1.8"
                                fill="white"
                            />
                        ))
                    )}
                </svg>

                {/* Dashboard illustration - bottom right */}
                <div style={{
                    position: 'absolute',
                    bottom: '40px',
                    right: '40px',
                    width: '320px',
                    height: '200px',
                    background: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '14px',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    padding: '18px',
                    zIndex: 1,
                    overflow: 'hidden'
                }}>
                    {/* Window dots */}
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }} />
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }} />
                    </div>
                    {/* Chart svg */}
                    <svg width="100%" height="140" viewBox="0 0 280 140" fill="none" preserveAspectRatio="none">
                        {/* Line chart */}
                        <path
                            d="M0,100 C40,80 60,60 90,50 C120,40 140,70 170,55 C200,40 230,20 280,30"
                            stroke="rgba(255,255,255,0.85)"
                            strokeWidth="2"
                            fill="none"
                        />
                        {/* Area fill */}
                        <path
                            d="M0,100 C40,80 60,60 90,50 C120,40 140,70 170,55 C200,40 230,20 280,30 L280,140 L0,140 Z"
                            fill="rgba(255,255,255,0.08)"
                        />
                        {/* Endpoint dot */}
                        <circle cx="170" cy="55" r="4" fill="white" />
                        {/* Bars */}
                        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                            <rect
                                key={i}
                                x={i * 34 + 6}
                                y={140 - (20 + (i % 4) * 12 + (i * 3))}
                                width="18"
                                height={20 + (i % 4) * 12 + (i * 3)}
                                fill="rgba(255,255,255,0.25)"
                                rx="2"
                            />
                        ))}
                    </svg>
                </div>

                {/* Centered brand block */}
                <div style={styles.leftContent}>
                    <div style={styles.logoIcon}>
                        <BoltIcon style={styles.logoIconInner} className="logo-bolt-pulse" />
                    </div>
                    <h1 style={styles.logoText}>AdVolt</h1>
                    <span style={styles.logoTagline}>Power Your Reach</span>
                    <div style={styles.taglineDivider} />
                    <p style={styles.brandText}>
                        The intelligent advertising platform that connects your brand with the right audience, everywhere they go.
                    </p>
                </div>
            </div>

            {/* Right Panel - Login Form */}
            <div style={styles.rightPanel}>
                <div style={styles.formContainer}>
                    {mobile && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '24px' }}>
                            <div style={{
                                width: '56px',
                                height: '56px',
                                borderRadius: '16px',
                                background: PURPLE_GRADIENT,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                marginBottom: '12px',
                                boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)'
                            }}>
                                <BoltIcon style={{ fontSize: '28px', color: '#fbbf24' }} className="logo-bolt-pulse" />
                            </div>
                            <span style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>AdVolt</span>
                        </div>
                    )}

                    <h1 style={styles.title}>Welcome back</h1>
                    <p style={styles.subtitle}>Sign in to your account</p>

                    <Form onSubmit={onLoginButtonClick} style={styles.formReset}>
                        {errorMessage && (
                            <FormAlert>
                                <Alert
                                    variant={emailNotVerified ? "warning" : "danger"}
                                    title={errorMessage}
                                    aria-live="polite"
                                    isInline
                                >
                                    {emailNotVerified && (
                                        <div style={{ marginTop: '8px' }}>
                                            {resendSent ? (
                                                <span style={{ color: '#3e8635', fontSize: '14px' }}>
                                                    Confirmation email resent!
                                                </span>
                                            ) : (
                                                <Button
                                                    variant="link"
                                                    onClick={handleResendFromLogin}
                                                    isLoading={isResending}
                                                    isDisabled={isResending}
                                                    style={{ padding: 0, fontSize: '14px' }}
                                                >
                                                    Resend confirmation email
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </Alert>
                            </FormAlert>
                        )}

                        {/* Email Field */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.fieldLabel} htmlFor="login-email">Email</label>
                            <BorderedInput
                                id="login-email"
                                value={userNameValue}
                                onChange={handleUsernameChange}
                                type="email"
                                placeholder="you@example.com"
                                icon={<EnvelopeIcon />}
                            />
                        </div>

                        {/* Password Field */}
                        <div style={styles.fieldGroup}>
                            <label style={styles.fieldLabel} htmlFor="login-password">Password</label>
                            <BorderedInput
                                id="login-password"
                                value={passwordValue}
                                onChange={handlePasswordChange}
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Enter your password"
                                icon={<LockIcon />}
                                rightAdornment={
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword((s) => !s)}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            padding: 0,
                                            color: '#9ca3af',
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                                    </button>
                                }
                            />
                        </div>

                        {/* Remember Me */}
                        <div style={styles.checkboxContainer}>
                            <Checkbox
                                label="Keep me logged in for 30 days"
                                id="remember-me"
                                isChecked={isRememberMeChecked}
                                onChange={onRememberMeClick}
                            />
                        </div>

                        {/* Submit Button */}
                        <Button
                            variant="primary"
                            type="submit"
                            isLoading={isLoading}
                            isDisabled={!isFormValid || isLoading}
                            isBlock
                            style={!isFormValid || isLoading ? styles.submitButtonDisabled : styles.submitButton}
                        >
                            {isLoading ? 'Signing in...' : 'Sign in'}
                        </Button>

                        {/* Forgot password */}
                        <div style={styles.forgotLinkRow}>
                            <a href="/forgot-password" style={styles.link}>Forgot password?</a>
                        </div>

                        {/* Divider */}
                        <div style={styles.dividerRow}>
                            <div style={styles.dividerLine} />
                            <span style={styles.dividerLabel}>or</span>
                            <div style={styles.dividerLine} />
                        </div>

                        {/* Signup */}
                        <div style={styles.signupRow}>
                            Don't have an account?{' '}
                            <a
                                href="#"
                                style={styles.link}
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleCreateAccountModalToggle();
                                }}
                            >
                                Sign up
                            </a>
                        </div>

                        {/* Footer */}
                        <div style={styles.footerContainer}>
                            <a href="#" style={styles.footerLink}>Terms of Use</a>
                            <a href="#" style={styles.footerLink}>Privacy Policy</a>
                            <a href="#" style={styles.footerLink}>Help</a>
                        </div>
                    </Form>
                </div>
            </div>

            <CreateAccountComponent
                isModalOpen={isCreateAccountModalOpen}
                onModalClose={handleCreateAccountModalToggle}
                setEmailAddress={handleUsernameChange}
            />
        </div>
    );
}

export {LoginComponent}
